import { useCallback, useEffect, useRef, useState, type RefObject } from "react"

const DEFAULT_IDLE_MS = 2500

/**
 * Показывает «активность» указателя на контейнере; после idleMs без событий — idle.
 * Пока указатель над панелью контролов (holdVisible), idle не срабатывает.
 */
export function usePointerIdle(options: {
  containerRef: RefObject<HTMLElement | null>
  enabled: boolean
  idleMs?: number
}): {
  active: boolean
  onControlsPointerEnter: () => void
  onControlsPointerLeave: () => void
  /** Последний pointerdown был тач-тапом по скрытым контролам — только показал их. */
  wasRevealTap: () => boolean
} {
  const { containerRef, enabled, idleMs = DEFAULT_IDLE_MS } = options
  const [active, setActive] = useState(true)
  const activeRef = useRef(true)
  activeRef.current = active
  const holdVisibleRef = useRef(false)
  const lastDownRevealedRef = useRef(false)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const clearTimer = useCallback(() => {
    if (timerRef.current != null) {
      clearTimeout(timerRef.current)
      timerRef.current = null
    }
  }, [])

  const scheduleIdle = useCallback(() => {
    clearTimer()
    if (!enabled || holdVisibleRef.current) return
    timerRef.current = setTimeout(() => {
      timerRef.current = null
      if (!holdVisibleRef.current) setActive(false)
    }, idleMs)
  }, [clearTimer, enabled, idleMs])

  const bump = useCallback(() => {
    if (!enabled) return
    setActive(true)
    scheduleIdle()
  }, [enabled, scheduleIdle])

  const onControlsPointerEnter = useCallback(() => {
    holdVisibleRef.current = true
    clearTimer()
    if (enabled) setActive(true)
  }, [clearTimer, enabled])

  const onControlsPointerLeave = useCallback(() => {
    holdVisibleRef.current = false
    if (enabled) scheduleIdle()
  }, [enabled, scheduleIdle])

  useEffect(() => {
    if (!enabled) {
      clearTimer()
      holdVisibleRef.current = false
      lastDownRevealedRef.current = false
      setActive(true)
      return
    }

    const el = containerRef.current
    if (!el) return

    setActive(true)
    scheduleIdle()

    const onPointerMove = () => bump()
    const onPointerDown = (e: PointerEvent) => {
      // Тач-тап при скрытых контролах только показывает их: click, который придёт
      // следом, не должен переключать play (см. wasRevealTap у потребителя).
      lastDownRevealedRef.current = !activeRef.current && e.pointerType === "touch"
      bump()
    }
    const onKeyDown = () => bump()

    el.addEventListener("pointermove", onPointerMove)
    el.addEventListener("pointerdown", onPointerDown)
    document.addEventListener("keydown", onKeyDown)

    return () => {
      el.removeEventListener("pointermove", onPointerMove)
      el.removeEventListener("pointerdown", onPointerDown)
      document.removeEventListener("keydown", onKeyDown)
      clearTimer()
    }
  }, [bump, clearTimer, containerRef, enabled, scheduleIdle])

  const wasRevealTap = useCallback(() => lastDownRevealedRef.current, [])

  return { active, onControlsPointerEnter, onControlsPointerLeave, wasRevealTap }
}
